import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { debounce } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import {
  ChannelPartnerSummary,
  PartnerLeadJobType,
  PartnerLeadProductType,
  createPartnerLead,
  getChannelPartners,
} from '../../services/channelPartners';
import {
  AddressFormState,
  addressFormToPayload,
  emptyAddressForm,
  validateAddressForm,
} from './partnerFormFields';
import { pretty } from './PartnerLeads';

// Mirrors PartnerLead.PRODUCT_TYPES / JOB_TYPES / EQUIPMENT_TYPES
// (nouvakserverapp/src/models/PartnerLeads/partnerLead.model.js) — the admin
// panel has no endpoint that exposes these, so they're kept in sync by hand.
const PRODUCT_TYPES: PartnerLeadProductType[] = ['air_conditioning', 'heat_pump', 'boiler', 'other'];
const JOB_TYPES: PartnerLeadJobType[] = ['service', 'repair', 'installation'];
const EQUIPMENT_TYPES = [
  'split',
  'window',
  'cassette',
  'ducted',
  'tower',
  'vrv_vrf',
  'chiller',
  'wall_mount',
  'floor_mount',
  'out_door',
  'hot_water_tank',
  'radiator',
  'boiler',
];

// Mirrors the UK mobile sanitize/validate pattern in ChannelPartners/index.tsx
// (not exported from there, so kept local here too).
const sanitizeUkMobileDigits = (raw: string) => {
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits.slice(0, 10);
};
const isValidUkMobileDigits = (digits: string) => digits.length === 10 && digits[0] === '7';

const fieldSx = {
  '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
};

interface AddPartnerLeadDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const AddPartnerLeadDialog: React.FC<AddPartnerLeadDialogProps> = ({ open, onClose, onCreated }) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [partnerOptions, setPartnerOptions] = useState<ChannelPartnerSummary[]>([]);
  const [partnerSearchLoading, setPartnerSearchLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<ChannelPartnerSummary | null>(null);

  const [productType, setProductType] = useState<PartnerLeadProductType | ''>('');
  const [jobType, setJobType] = useState<PartnerLeadJobType | ''>('');
  const [equipmentType, setEquipmentType] = useState('');
  const [quantity, setQuantity] = useState(1);

  const [customerName, setCustomerName] = useState('');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [addressForm, setAddressForm] = useState<AddressFormState>(emptyAddressForm);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const resetForm = () => {
    setPartnerOptions([]);
    setSelectedPartner(null);
    setProductType('');
    setJobType('');
    setEquipmentType('');
    setQuantity(1);
    setCustomerName('');
    setPhoneDigits('');
    setPhoneTouched(false);
    setAddressForm(emptyAddressForm);
    setNotes('');
  };

  useEffect(() => {
    if (open) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const searchPartners = debounce(async (query: string) => {
    if (!query.trim()) {
      setPartnerOptions([]);
      return;
    }
    try {
      setPartnerSearchLoading(true);
      const result = await getChannelPartners({ page: 1, limit: 10, search: query });
      setPartnerOptions(result.data || []);
    } catch (error) {
      setPartnerOptions([]);
    } finally {
      setPartnerSearchLoading(false);
    }
  }, 400);

  const handlePhoneChange = (value: string) => setPhoneDigits(sanitizeUkMobileDigits(value));
  const phoneInvalid = phoneTouched && phoneDigits.length > 0 && !isValidUkMobileDigits(phoneDigits);

  const handleCreate = async () => {
    setPhoneTouched(true);
    if (!selectedPartner) return showSnackbar('Select a channel partner', 'error');
    if (!productType) return showSnackbar('Select a product type', 'error');
    if (productType === 'air_conditioning' && !jobType) {
      return showSnackbar('Job type is required for air conditioning leads', 'error');
    }
    if (!customerName.trim()) return showSnackbar('Customer name is required', 'error');
    if (!isValidUkMobileDigits(phoneDigits)) {
      return showSnackbar('Enter a valid UK mobile number for the customer', 'error');
    }
    const addressError = validateAddressForm(addressForm);
    if (addressError) return showSnackbar(addressError, 'error');

    try {
      setCreating(true);
      await createPartnerLead({
        channelPartner: selectedPartner.id,
        productType,
        jobType: jobType || undefined,
        equipmentType: equipmentType || undefined,
        quantity,
        customerName: customerName.trim(),
        customerPhoneNumber: `+44${phoneDigits}`,
        address: addressFormToPayload(addressForm),
        notes: notes.trim() || undefined,
      });
      showSnackbar('Lead created', 'success');
      onCreated();
      onClose();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to create lead', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {SnackbarComponent}
      <Dialog
        open={open}
        onClose={() => !creating && onClose()}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#171717', color: '#fff', border: '1px solid #242424', borderRadius: 3, backgroundImage: 'none' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          Add Partner Lead
          <IconButton onClick={() => !creating && onClose()} sx={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: '#242424' }} />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <Autocomplete
              options={partnerOptions}
              value={selectedPartner}
              loading={partnerSearchLoading}
              getOptionLabel={(partner) => `${partner.fullName || 'Unnamed'} · ${partner.phoneNumber}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_, newValue) => searchPartners(newValue)}
              onChange={(_, newValue) => setSelectedPartner(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Channel partner *"
                  placeholder="Search by name or phone"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {partnerSearchLoading && <CircularProgress color="inherit" size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Divider sx={{ borderColor: '#242424' }}><Typography variant="caption" color="#64748b">Product</Typography></Divider>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Product type *"
                  value={productType}
                  onChange={(e) => {
                    const value = e.target.value as PartnerLeadProductType;
                    setProductType(value);
                    if (value !== 'air_conditioning') setJobType('');
                  }}
                  sx={fieldSx}
                >
                  {PRODUCT_TYPES.map((t) => <MenuItem key={t} value={t}>{pretty(t)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label={`Job type${productType === 'air_conditioning' ? ' *' : ' (optional)'}`}
                  value={jobType}
                  onChange={(e) => setJobType(e.target.value as PartnerLeadJobType)}
                  sx={fieldSx}
                >
                  <MenuItem value="">None</MenuItem>
                  {JOB_TYPES.map((t) => <MenuItem key={t} value={t}>{pretty(t)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Equipment (optional)"
                  value={equipmentType}
                  onChange={(e) => setEquipmentType(e.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value="">None</MenuItem>
                  {EQUIPMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{pretty(t)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1 }}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Divider sx={{ borderColor: '#242424' }}><Typography variant="caption" color="#64748b">Customer</Typography></Divider>
            <TextField
              label="Customer name *"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Customer mobile number *"
              value={phoneDigits}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => setPhoneTouched(true)}
              placeholder="7123 456789"
              error={phoneInvalid}
              helperText={phoneInvalid ? 'Enter a valid UK mobile number (10 digits, starting with 7)' : ' '}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#94a3b8', fontWeight: 600, pr: 0.5, borderRight: '1px solid #333' }}>+44</Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ maxLength: 11, inputMode: 'numeric' }}
              fullWidth
              sx={fieldSx}
            />

            <Divider sx={{ borderColor: '#242424' }}><Typography variant="caption" color="#64748b">Address</Typography></Divider>
            <Grid container spacing={1.5}>
              <Grid item xs={12}><TextField fullWidth sx={fieldSx} label="Line 1 *" value={addressForm.line1} onChange={(e) => setAddressForm((p) => ({ ...p, line1: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField fullWidth sx={fieldSx} label="Line 2" value={addressForm.line2} onChange={(e) => setAddressForm((p) => ({ ...p, line2: e.target.value }))} /></Grid>
              <Grid item xs={6}><TextField fullWidth sx={fieldSx} label="Town *" value={addressForm.town} onChange={(e) => setAddressForm((p) => ({ ...p, town: e.target.value }))} /></Grid>
              <Grid item xs={6}><TextField fullWidth sx={fieldSx} label="County" value={addressForm.county} onChange={(e) => setAddressForm((p) => ({ ...p, county: e.target.value }))} /></Grid>
              <Grid item xs={6}><TextField fullWidth sx={fieldSx} label="Postcode *" value={addressForm.postcode} onChange={(e) => setAddressForm((p) => ({ ...p, postcode: e.target.value.toUpperCase() }))} /></Grid>
            </Grid>

            <Divider sx={{ borderColor: '#242424' }}><Typography variant="caption" color="#64748b">Notes</Typography></Divider>
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              sx={fieldSx}
            />
          </Box>
        </DialogContent>
        <Divider sx={{ borderColor: '#242424' }} />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => !creating && onClose()} disabled={creating} sx={{ color: '#94a3b8', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{ backgroundColor: '#7B1FA2', '&:hover': { backgroundColor: '#5E167A' }, textTransform: 'none', borderRadius: '10px' }}
          >
            {creating ? 'Creating…' : 'Create Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddPartnerLeadDialog;
